import { useState, useMemo, useEffect, useCallback } from 'react';
import { useAuthGuard } from '@/hooks/useAuth';
import AdminLayout from '@/components/feature/AdminLayout';
import { supabase } from '@/lib/supabaseClient';
import { ARAC_DURUM_RENKLERI, ARAC_TIPLERI } from '@/lib/constants';
import AracModal from './components/AracModal';

interface Vehicle {
  id: number;
  ad: string;
  plaka: string;
  marka_model: string;
  arac_tipi: string;
  sofor: string;
  kapasite: number;
  durum: string;
  gunluk_gorev_sayisi: number;
  toplam_kazanc_eur: number;
  toplam_kazanc_usd: number;
  toplam_kazanc_tl: number;
}

export default function AraclarPage() {
  useAuthGuard();
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [durumFilter, setDurumFilter] = useState('');
  const [tipFilter, setTipFilter] = useState('');
  const [arama, setArama] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editVehicle, setEditVehicle] = useState<Vehicle | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<Vehicle | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    const { data, error: err } = await supabase.from('vehicles').select('*').order('ad');
    if (err) setError(err.message);
    else setVehicles(data || []);
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = useMemo(() => {
    return vehicles.filter(v => {
      const matchDurum = !durumFilter || v.durum === durumFilter;
      const matchTip = !tipFilter || v.arac_tipi === tipFilter;
      const q = arama.toLowerCase();
      const matchArama = !arama || v.ad?.toLowerCase().includes(q) || v.plaka?.toLowerCase().includes(q) || v.sofor?.toLowerCase().includes(q);
      return matchDurum && matchTip && matchArama;
    });
  }, [vehicles, durumFilter, tipFilter, arama]);

  const showSuccess = (msg: string) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 3000);
  };

  const handleSave = async (data: Omit<Vehicle, 'id'>) => {
    setSaving(true);
    if (editVehicle) {
      const { error: err } = await supabase.from('vehicles').update(data).eq('id', editVehicle.id);
      if (err) { setError(err.message); setSaving(false); return; }
      setVehicles(prev => prev.map(v => v.id === editVehicle.id ? { ...v, ...data } : v));
      showSuccess('Araç güncellendi.');
    } else {
      const { data: inserted, error: err } = await supabase.from('vehicles').insert(data).select().maybeSingle();
      if (err) { setError(err.message); setSaving(false); return; }
      if (inserted) setVehicles(prev => [...prev, inserted]);
      showSuccess('Araç eklendi.');
    }
    setSaving(false);
    setShowModal(false);
    setEditVehicle(null);
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    const idToDelete = deleteConfirm.id;
    setDeleting(true);
    setDeleteConfirm(null);

    const { data, error } = await supabase.functions.invoke('manage-data', {
      body: { action: 'delete', table: 'vehicles', id: idToDelete },
    });

    setDeleting(false);

    if (error || !data?.success) {
      setError('Silinemedi: ' + (error?.message || data?.error || 'Bilinmeyen hata'));
      await fetchData();
      return;
    }
    setVehicles(prev => prev.filter(v => v.id !== idToDelete));
    showSuccess('Araç silindi.');
  };

  return (
    <AdminLayout>
      {showModal && (
        <AracModal
          vehicle={editVehicle}
          onSave={handleSave}
          onClose={() => { setShowModal(false); setEditVehicle(null); }}
          saving={saving}
        />
      )}

      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-vip-dark border border-red-500/20 rounded-xl p-6 w-full max-w-sm">
            <div className="w-12 h-12 rounded-full bg-red-500/15 flex items-center justify-center mx-auto mb-4">
              <i className="ri-delete-bin-line text-red-400 text-xl"></i>
            </div>
            <h3 className="text-base font-semibold text-white text-center mb-2">Aracı Sil</h3>
            <p className="text-sm text-gray-400 text-center mb-1"><strong className="text-white">{deleteConfirm.ad}</strong></p>
            <p className="text-xs text-gray-500 text-center mb-5">{deleteConfirm.plaka}</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 py-2.5 rounded-md bg-vip-black border border-vip-gold/10 text-gray-400 text-sm hover:text-white transition-colors whitespace-nowrap">İptal</button>
              <button onClick={handleDelete} disabled={deleting} className="flex-1 py-2.5 rounded-md bg-red-500/20 border border-red-500/30 text-red-400 text-sm hover:bg-red-500/30 transition-colors disabled:opacity-50 whitespace-nowrap">
                {deleting ? 'Siliniyor...' : 'Sil'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-xl md:text-2xl font-display font-bold text-white">Araçlar</h2>
          <p className="text-sm text-gray-400 mt-1">Filo yönetimi, araç durumları ve kazanç özeti</p>
        </div>
        <button onClick={() => { setEditVehicle(null); setShowModal(true); }}
          className="px-4 py-2 bg-vip-gold text-vip-black text-sm font-semibold rounded-md hover:bg-vip-gold-light transition-colors whitespace-nowrap">
          <i className="ri-add-line mr-1"></i>Yeni Araç
        </button>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-3 mb-4 flex items-center gap-2">
          <i className="ri-error-warning-line text-red-400"></i>
          <p className="text-sm text-red-400">{error}</p>
          <button onClick={() => setError('')} className="ml-auto text-gray-500 hover:text-white"><i className="ri-close-line"></i></button>
        </div>
      )}

      {successMsg && (
        <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-4 py-3 mb-4 flex items-center gap-2">
          <i className="ri-check-line text-emerald-400"></i>
          <p className="text-sm text-emerald-400">{successMsg}</p>
        </div>
      )}

      <div className="flex flex-col sm:flex-row flex-wrap gap-3 mb-5">
        <div className="relative flex-1 min-w-[200px]">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
            <i className="ri-search-line text-gray-500 text-sm"></i>
          </div>
          <input type="text" placeholder="Araç, plaka veya şoför ara..." value={arama} onChange={e => setArama(e.target.value)}
            className="w-full bg-vip-dark border border-vip-gold/15 rounded-md pl-9 pr-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-vip-gold/40 transition-colors" />
        </div>
        <select value={durumFilter} onChange={e => setDurumFilter(e.target.value)}
          className="bg-vip-dark border border-vip-gold/15 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-vip-gold/40 transition-colors">
          <option value="">Tüm Durumlar</option>
          {['Müsait','Görevde','Bakımda'].map(d => <option key={d} value={d}>{d}</option>)}
        </select>
        <select value={tipFilter} onChange={e => setTipFilter(e.target.value)}
          className="bg-vip-dark border border-vip-gold/15 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-vip-gold/40 transition-colors">
          <option value="">Tüm Araç Tipleri</option>
          {ARAC_TIPLERI.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <div className="bg-vip-dark border border-vip-gold/10 rounded-md px-3 py-2 text-sm text-gray-400">
          <span className="text-vip-gold font-semibold">{filtered.length}</span> araç
        </div>
      </div>

      {loading ? (
        <div className="space-y-2">
          {[...Array(5)].map((_, i) => <div key={i} className="h-14 bg-vip-dark rounded-lg animate-pulse"></div>)}
        </div>
      ) : (
        <>
          {/* Desktop Table */}
          <div className="hidden md:block bg-vip-dark border border-vip-gold/10 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-vip-gold/10 bg-vip-black/50">
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">Araç Adı</th>
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">Plaka</th>
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">Marka / Model</th>
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">Tip</th>
                    <th className="text-center py-3 px-4 text-gray-400 font-medium">Kapasite</th>
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">Şoför</th>
                    <th className="text-center py-3 px-4 text-gray-400 font-medium">Durum</th>
                    <th className="text-right py-3 px-4 text-gray-400 font-medium">Toplam Kazanç</th>
                    <th className="text-center py-3 px-4 text-gray-400 font-medium">İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(v => (
                    <tr key={v.id} className="border-b border-vip-gold/5 hover:bg-white/5 transition-colors group">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-md bg-vip-gold/10 flex items-center justify-center"><i className="ri-roadster-line text-vip-gold text-xs"></i></div>
                          <span className="text-white font-medium">{v.ad}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-gray-300 font-mono text-xs">{v.plaka}</td>
                      <td className="py-3 px-4 text-gray-400 text-xs">{v.marka_model}</td>
                      <td className="py-3 px-4 text-gray-300">{v.arac_tipi}</td>
                      <td className="py-3 px-4 text-center text-white">{v.kapasite}</td>
                      <td className="py-3 px-4 text-gray-300">{v.sofor}</td>
                      <td className="py-3 px-4 text-center">
                        <span className={`text-xs px-2 py-1 rounded-md ${ARAC_DURUM_RENKLERI[v.durum] || 'bg-gray-500/20 text-gray-400'}`}>{v.durum}</span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="space-y-0.5">
                          {v.toplam_kazanc_eur > 0 && <p className="text-xs text-vip-gold">{v.toplam_kazanc_eur} €</p>}
                          {v.toplam_kazanc_usd > 0 && <p className="text-xs text-blue-400">{v.toplam_kazanc_usd} $</p>}
                          {v.toplam_kazanc_tl > 0 && <p className="text-xs text-emerald-400">{Number(v.toplam_kazanc_tl).toLocaleString('tr-TR')} ₺</p>}
                          {!v.toplam_kazanc_eur && !v.toplam_kazanc_usd && !v.toplam_kazanc_tl && <p className="text-xs text-gray-500">-</p>}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-center gap-1">
                          <button onClick={() => { setEditVehicle(v); setShowModal(true); }}
                            className="w-7 h-7 rounded-md hover:bg-vip-gold/10 text-gray-500 hover:text-vip-gold transition-colors flex items-center justify-center">
                            <i className="ri-edit-line text-xs"></i>
                          </button>
                          <button onClick={() => setDeleteConfirm(v)}
                            className="w-7 h-7 rounded-md hover:bg-red-500/10 text-gray-500 hover:text-red-400 transition-colors flex items-center justify-center">
                            <i className="ri-delete-bin-line text-xs"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden grid grid-cols-1 gap-3">
            {filtered.map(v => (
              <div key={v.id} className="bg-vip-dark border border-vip-gold/10 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-md bg-vip-gold/10 flex items-center justify-center"><i className="ri-roadster-line text-vip-gold text-xs"></i></div>
                    <div>
                      <p className="text-sm font-medium text-white">{v.ad}</p>
                      <p className="text-xs text-gray-500 font-mono">{v.plaka}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className={`text-xs px-2 py-1 rounded-md ${ARAC_DURUM_RENKLERI[v.durum] || 'bg-gray-500/20 text-gray-400'}`}>{v.durum}</span>
                    <button onClick={() => { setEditVehicle(v); setShowModal(true); }} className="w-7 h-7 rounded-md hover:bg-vip-gold/10 text-gray-500 hover:text-vip-gold transition-colors flex items-center justify-center"><i className="ri-edit-line text-xs"></i></button>
                    <button onClick={() => setDeleteConfirm(v)} className="w-7 h-7 rounded-md hover:bg-red-500/10 text-gray-500 hover:text-red-400 transition-colors flex items-center justify-center"><i className="ri-delete-bin-line text-xs"></i></button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="bg-vip-black/50 rounded-md p-2"><p className="text-gray-500 mb-0.5">Şoför</p><p className="text-gray-300">{v.sofor}</p></div>
                  <div className="bg-vip-black/50 rounded-md p-2"><p className="text-gray-500 mb-0.5">Kapasite</p><p className="text-gray-300">{v.kapasite} Kişi</p></div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {!loading && filtered.length === 0 && (
        <div className="bg-vip-dark border border-vip-gold/10 rounded-lg p-8 text-center">
          <p className="text-sm text-gray-400">Filtrelere uygun araç bulunamadı.</p>
        </div>
      )}
    </AdminLayout>
  );
}